import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { authApi } from "@/lib/api";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { AuthLayout } from "../components/auth-layout";
import {
  CompactCard,
  CompactField,
  CompactFormActionButton,
  CompactFormFooter,
  CompactFormHeader,
} from "@/components/compact-ui";
import "@/components/compact-ui/compact-form.css";
import { isFormSaveDisabled } from "@/lib/form-save-disabled";

const resetSchema = z
  .object({
    newPassword: z
      .string()
      .min(8, "Mínimo 8 caracteres")
      .regex(/[A-Z]/, "Al menos una mayúscula")
      .regex(/[a-z]/, "Al menos una minúscula")
      .regex(/[0-9]/, "Al menos un número")
      .regex(/[^A-Za-z0-9]/, "Al menos un carácter especial"),
    confirmPassword: z.string().min(1, "Confirma la contraseña"),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Las contraseñas no coinciden",
    path: ["confirmPassword"],
  });

type ResetFormData = z.infer<typeof resetSchema>;

export const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ResetFormData>({
    resolver: zodResolver(resetSchema),
    mode: "onTouched",
    reValidateMode: "onChange",
    defaultValues: { newPassword: "", confirmPassword: "" },
  });

  const newPasswordValue = watch("newPassword");
  const confirmPasswordValue = watch("confirmPassword");

  const saveDisabled = isFormSaveDisabled({
    canSave: !loading && Boolean(token),
    hasRequiredValues:
      Boolean(newPasswordValue) && Boolean(confirmPasswordValue),
    hasRequiredFieldErrors: Boolean(
      errors.newPassword || errors.confirmPassword,
    ),
  });

  useEffect(() => {
    if (!token) {
      setError("Token de recuperación no válido o faltante.");
    }
  }, [token]);

  const onSubmit = async (data: ResetFormData) => {
    setError(null);
    if (!token) return;
    try {
      setLoading(true);
      await authApi.resetPassword(token, data.newPassword);
      setSuccess(true);
    } catch (err: unknown) {
      const msg =
        (err as { message?: string }).message ||
        "El token es inválido o ha expirado.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <AuthLayout>
        <div className="compact-form">
          <CompactFormHeader badge="Contraseña restablecida" />
          <div className="compact-form-body">
            <CompactCard title="Contraseña restablecida">
              <p className="text-[11px] text-muted-foreground">
                Tu contraseña ha sido restablecida correctamente. Ya puedes
                iniciar sesión con tu nueva contraseña.
              </p>
            </CompactCard>
          </div>
          <CompactFormFooter>
            <CompactFormActionButton
              variant="primary"
              label="Ir al login"
              hideIcon
              onClick={() => navigate("/login")}
            />
          </CompactFormFooter>
        </div>
      </AuthLayout>
    );
  }

  if (!token) {
    return (
      <AuthLayout>
        <div className="compact-form">
          <CompactFormHeader badge="Token inválido" />
          <div className="compact-form-body">
            <CompactCard title="Token inválido">
              <p className="text-[11px] text-muted-foreground">
                El enlace de recuperación no es válido o ha expirado. Solicita
                uno nuevo desde la página de recuperación de contraseña.
              </p>
            </CompactCard>
          </div>
          <CompactFormFooter>
            <CompactFormActionButton
              variant="secondary"
              label="Volver al login"
              onClick={() => navigate("/login")}
            />
            <CompactFormActionButton
              variant="primary"
              label="Solicitar nuevo enlace"
              hideIcon
              onClick={() => navigate("/forgot-password")}
            />
          </CompactFormFooter>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="compact-form">
        <form onSubmit={handleSubmit(onSubmit)} noValidate autoComplete="off">
          <CompactFormHeader badge="Nueva contraseña" />

          <div className="compact-form-body">
            <CompactCard
              title="Nueva contraseña"
              hint="Mínimo 8 caracteres con mayúscula, minúscula, número y carácter especial."
            >
              <CompactField
                label="Contraseña"
                htmlFor="newPassword"
                required
                error={errors.newPassword?.message}
              >
                <Input
                  id="newPassword"
                  type="password"
                  placeholder="Ej. MiClave#2024"
                  disabled={loading}
                  autoComplete="new-password"
                  {...register("newPassword")}
                />
              </CompactField>

              <CompactField
                label="Confirmar"
                htmlFor="confirmPassword"
                required
                error={errors.confirmPassword?.message}
              >
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Repite la contraseña"
                  disabled={loading}
                  autoComplete="new-password"
                  {...register("confirmPassword")}
                />
              </CompactField>

              {error ? (
                <p
                  className="compact-span-all text-[11px] text-destructive"
                  role="alert"
                >
                  {error}
                </p>
              ) : null}
            </CompactCard>
          </div>

          <CompactFormFooter
            cancel={{
              label: "Volver al login",
              onClick: () => navigate("/login"),
              disabled: loading,
            }}
            submit={{
              label: "Restablecer contraseña",
              loadingLabel: "Restableciendo…",
              disabled: saveDisabled,
              isSubmitting: loading,
            }}
          />
        </form>
      </div>
    </AuthLayout>
  );
};
